import { Heading, Text } from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import DesignImage1 from '@/assets/gift-cards/design-1.png';
import DesignImage2 from '@/assets/gift-cards/design-2.png';
import DesignImage3 from '@/assets/gift-cards/design-3.png';
import DesignImage4 from '@/assets/gift-cards/design-4.png';
import DesignImage5 from '@/assets/gift-cards/design-5.png';
import DesignImage6 from '@/assets/gift-cards/design-6.png';
import DesignImage7 from '@/assets/gift-cards/design-7.png';
import DesignImage8 from '@/assets/gift-cards/design-8.png';
import DesignImage9 from '@/assets/gift-cards/design-9.png';
import AspectRatio from '@/components/common/AspectRatio';
import Button, {
  ButtonShape,
  ButtonSize,
  ButtonVariants,
} from '@/components/switchback/Button/Button';
import { ARROW_RIGHT } from '@/components/switchback/Icon/assets';
import Icon, { IconSize } from '@/components/switchback/Icon/IconComponent';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { GIFTS_URL } from '@/constants/urls';
import { useBreakpoint } from '@/hooks/useBreakpoint';

enum EPaginationButtonVariant {
  next,
  prev,
}

type TPaginationButton = {
  onClick: () => void;
  variant: EPaginationButtonVariant;
  disabled?: boolean;
};

const PaginationButton = ({ onClick, variant, disabled }: TPaginationButton) => {
  return (
    <Button
      className="mr-4 last:mr-0"
      onClick={onClick}
      shape={ButtonShape.circle}
      variant={ButtonVariants.whiteContained}
      size={ButtonSize.small}
      disabled={disabled}>
      <Icon
        name={ARROW_RIGHT}
        size={IconSize.normal}
        className={variant === EPaginationButtonVariant.prev ? 'transform rotate-180' : undefined}
      />
    </Button>
  );
};

export const PerfectDesignSection = () => {
  const { isMobile } = useBreakpoint();
  const [designPos, setDesignPos] = useState<number>(0);
  const designs = [
    DesignImage1,
    DesignImage2,
    DesignImage3,
    DesignImage4,
    DesignImage5,
    DesignImage6,
    DesignImage7,
    DesignImage8,
    DesignImage9,
  ];
  return (
    <div className="pt-10 pb-5 mx-auto">
      <div className="flex flex-col items-center mx-auto gap-5 lg:justify-center lg:max-w-[80%]">
        <Heading className="font-bold leading-10 lg:leading-none lg:text-center text-[32px]">
          <FormattedMessage defaultMessage="Choose the perfect design" id="//dDFN" />
        </Heading>
        <Text className="lg:text-center">
          <FormattedMessage
            defaultMessage="Once you choose a card design and gift amount for the recipient, you can decide whether you want it sent digitally or in the mail. See some of our design options below."
            id="//dk2Y"
          />
        </Text>
      </div>
      <div className="hidden py-10 sm:grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {designs.map((design, index) => (
          <AspectRatio key={index} ratio="3:2" hasImageHover className="!rounded-lg">
            <ResponsiveImage src={design.src} alt={`Gift card design ${index + 1}`} />
          </AspectRatio>
        ))}
      </div>
      <div className="flex flex-col items-center my-5 sm:hidden gap-5">
        {designs[designPos] && (
          <ResponsiveImage
            className="rounded-md"
            src={designs[designPos].src}
            alt={`Gift card design ${designPos + 1}`}
          />
        )}
        <div>
          <PaginationButton
            onClick={() => setDesignPos(val => val - 1)}
            disabled={designPos === 0}
            variant={EPaginationButtonVariant.prev}
          />
          <PaginationButton
            onClick={() => setDesignPos(val => val + 1)}
            disabled={designPos === designs.length - 1}
            variant={EPaginationButtonVariant.next}
          />
        </div>
      </div>
      <div className="flex justify-center">
        <Button href={GIFTS_URL} rel="noreferrer" target={!isMobile ? '_blank' : undefined}>
          {isMobile ? (
            <FormattedMessage defaultMessage="Buy one now" id="uV1XpL" />
          ) : (
            <FormattedMessage defaultMessage="Start gifting" id="4OwdQh" />
          )}
        </Button>
      </div>
    </div>
  );
};
